import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  InputAdornment,
  Tooltip,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Delete as DeleteIcon,
  PersonAdd as AddIcon,
  Search as SearchIcon,
  School as ModuleIcon,
} from '@mui/icons-material';
import { adminApi, courseApi, UserProfile, Assignment } from '../services/api';

interface Course {
  id: string;
  title: string;
  slug: string;
  workArea?: { name: string } | null;
}

export default function AdminAssignmentsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [selectedCourseId, setSelectedCourseId] = useState<string>(searchParams.get('courseId') || '');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch courses
  const { data: coursesData, isLoading: loadingCourses } = useQuery({
    queryKey: ['admin', 'courses'],
    queryFn: () => courseApi.getCourses().then((r) => r.data),
  });

  const courses: Course[] = coursesData?.data || [];

  // Fetch assignments for selected course
  const { data: assignmentsData, isLoading: loadingAssignments } = useQuery({
    queryKey: ['admin', 'assignments', selectedCourseId],
    queryFn: () => adminApi.getModuleAssignments(selectedCourseId).then((r) => r.data),
    enabled: !!selectedCourseId,
  });

  // Fetch all users for add dialog
  const { data: usersData } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => adminApi.getUsers().then((r) => r.data),
    enabled: addDialogOpen,
  });

  const allUsers: UserProfile[] = Array.isArray(usersData) ? usersData : (usersData as any)?.users || [];

  // Filter out already assigned users
  const assignedUserIds = new Set(assignmentsData?.assignments?.map((a: Assignment) => a.userId) || []);
  const availableUsers = allUsers.filter((u) => !assignedUserIds.has(u.id));

  // Filter by search term
  const filteredUsers = availableUsers.filter(
    (u) =>
      (u.displayName?.toLowerCase() || '').includes(userSearchTerm.toLowerCase()) ||
      (u.email?.toLowerCase() || '').includes(userSearchTerm.toLowerCase())
  );

  // Assign users mutation
  const assignMutation = useMutation({
    mutationFn: (userIds: string[]) => adminApi.assignUsers(selectedCourseId, userIds),
    onSuccess: (response) => {
      const data = response.data as any;
      queryClient.invalidateQueries({ queryKey: ['admin', 'assignments', selectedCourseId] });
      setAddDialogOpen(false);
      setSelectedUsers([]);
      setSnackbar({
        open: true,
        message: `${data.assigned} Benutzer zugewiesen`,
        severity: 'success',
      });
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: 'Fehler beim Zuweisen',
        severity: 'error',
      });
    },
  });

  // Remove assignment mutation
  const removeMutation = useMutation({
    mutationFn: (userId: string) => adminApi.removeAssignment(selectedCourseId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'assignments', selectedCourseId] });
      setSnackbar({
        open: true,
        message: 'Zuweisung entfernt',
        severity: 'success',
      });
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: 'Fehler beim Entfernen',
        severity: 'error',
      });
    },
  });

  const handleToggleUser = (userId: string) => {
    setSelectedUsers((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const handleAssignUsers = () => {
    if (selectedUsers.length > 0) {
      assignMutation.mutate(selectedUsers);
    }
  };

  const handleRemoveAssignment = (userId: string) => {
    removeMutation.mutate(userId);
  };

  const selectedCourse = courses.find((c) => c.id === selectedCourseId);

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component="button" underline="hover" color="inherit" onClick={() => navigate('/admin')}>
          Admin
        </Link>
        <Typography color="text.primary">Modul-Zuweisungen</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4">Modul-Zuweisungen</Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Module an Benutzer zuweisen
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<BackIcon />} onClick={() => navigate('/admin')}>
          Zurueck
        </Button>
      </Box>

      {/* Module Selection */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <FormControl fullWidth>
          <InputLabel>Kurs auswählen</InputLabel>
          <Select
            value={selectedCourseId}
            label="Kurs auswählen"
            onChange={(e) => setSelectedCourseId(e.target.value)}
            disabled={loadingCourses}
          >
            {courses.map((course) => (
              <MenuItem key={course.id} value={course.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ModuleIcon fontSize="small" color="primary" />
                  {course.title}
                  {course.workArea && (
                    <Chip size="small" label={course.workArea.name} variant="outlined" sx={{ ml: 1 }} />
                  )}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {/* Assignments Table */}
      {selectedCourseId && (
        <Paper>
          <Box
            sx={{
              p: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="h6">
              Zuweisungen für: {selectedCourse?.title}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddDialogOpen(true)}
            >
              Benutzer zuweisen
            </Button>
          </Box>

          {loadingAssignments ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : assignmentsData?.assignments?.length === 0 ? (
            <Alert severity="info" sx={{ m: 2 }}>
              Noch keine Benutzer zugewiesen. Klicken Sie auf "Benutzer zuweisen" um zu beginnen.
            </Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Benutzer</TableCell>
                    <TableCell>E-Mail</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Zugewiesen am</TableCell>
                    <TableCell align="right">Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assignmentsData?.assignments?.map((assignment: Assignment) => (
                    <TableRow key={assignment.id} hover>
                      <TableCell>
                        <Typography fontWeight={500}>
                          {assignment.user.displayName || 'Unbekannt'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {assignment.user.email || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={assignment.status}
                          color={
                            assignment.status === 'completed'
                              ? 'success'
                              : assignment.status === 'in_progress'
                              ? 'warning'
                              : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(assignment.enrolledAt).toLocaleDateString('de-DE')}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Zuweisung entfernen">
                          <IconButton
                            color="error"
                            onClick={() => handleRemoveAssignment(assignment.userId)}
                            disabled={removeMutation.isPending}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {!selectedCourseId && (
        <Alert severity="info">
          Bitte wählen Sie ein Lernmodul aus, um die Zuweisungen zu verwalten.
        </Alert>
      )}

      {/* Add Users Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Benutzer zuweisen</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Benutzer suchen..."
            value={userSearchTerm}
            onChange={(e) => setUserSearchTerm(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            size="small"
          />

          {filteredUsers.length === 0 ? (
            <Alert severity="info">
              {availableUsers.length === 0
                ? 'Alle Benutzer sind bereits zugewiesen.'
                : 'Keine Benutzer gefunden.'}
            </Alert>
          ) : (
            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {filteredUsers.map((user) => (
                <ListItem key={user.id} disablePadding>
                  <ListItemButton onClick={() => handleToggleUser(user.id)} dense>
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={selectedUsers.includes(user.id)}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={user.displayName || 'Unbekannt'}
                      secondary={user.email}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}

          {selectedUsers.length > 0 && (
            <Typography variant="body2" color="primary" sx={{ mt: 2 }}>
              {selectedUsers.length} Benutzer ausgewählt
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleAssignUsers}
            disabled={selectedUsers.length === 0 || assignMutation.isPending}
          >
            {assignMutation.isPending ? 'Wird zugewiesen...' : 'Zuweisen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
